"use client";
import Link from "next/link";
import { Logo } from "@/assets/icons";
import { Button } from "./ui/button";
import { useEffect, useState } from "react";
import MobileMenu from "./MobileMenu";
import { useRouter, usePathname } from "next/navigation";

const NAV_ITEMS = [
  { id: "home", label: "Home", link: "/" },
  { id: "design-work", label: "Design Work" },
  { id: "what-we-do", label: "What We Do" },
  { id: "about", label: "About" },
  { id: "cost-calculator", label: "Cost Calculator", link: "/cost-calculator" },
];

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "design-work", "what-we-do", "about"];
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNavigation = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname !== "/") {
      await router.push("/");
      setTimeout(() => scrollToElement(id), 300);
    } else {
      scrollToElement(id);
    }
  };

  const handleNavClick = (id: string, e: React.MouseEvent) =>
    handleNavigation(id, e);
  const handleStartProject = (e: React.MouseEvent) =>
    handleNavigation("contact-us", e);

  return (
    <nav className="w-full fixed bg-white/65 backdrop-blur-md top-0 z-[222222]">
      <div className="max-w-[1440px] py-2 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0 items-center flex">
            <Link
              href="/"
              className="text-2xl flex gap-2 font-bold text-primary"
            >
              <Logo className="w-9 h-9" />
              <span>Digital Thrive</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {NAV_ITEMS.map((item) =>
              item.link ? (
                <Link
                  key={item.label}
                  href={item.link}
                  className="text-primary relative overflow-hidden group flex items-center justify-center h-10 px-3"
                >
                  <span className="absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform translate-y-0 group-hover:translate-y-full whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform -translate-y-full font-light group-hover:translate-y-0 text-primary-aqua whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="invisible">{item.label}</span>
                </Link>
              ) : (
                <Link
                  key={item.label}
                  href={`#${item.id}`}
                  onClick={(e) => handleNavClick(item.id, e)}
                  className="text-primary relative overflow-hidden group flex items-center justify-center h-10 px-3"
                >
                  <span className="absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform translate-y-0 group-hover:translate-y-full whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform -translate-y-full font-light group-hover:translate-y-0 text-primary-aqua whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="invisible">{item.label}</span>
                </Link>
              )
            )}
          </div>

          <div className="flex items-center">
            <Button className="hidden sm:flex" onClick={handleStartProject}>
              Start a project
            </Button>
            <button
              className="md:hidden flex flex-col items-end justify-center"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <span className="block w-6 h-0.5 bg-primary mb-2"></span>
              <span className="block w-8 h-0.5 bg-primary"></span>
            </button>
          </div>
        </div>
      </div>
      <section>
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          handleNavClick={handleNavClick}
        />
      </section>
    </nav>
  );
};

export default Navbar;
